import { Pie } from "react-chartjs-2";
import Table from "react-bootstrap/Table";
import formatNumber from "format-number";
import { SpinnerBlock } from "../Utils/CustomComponent";
import FormBox from "./FormBox/FormBox";
import useFetchTableData from "../useFetchTableData";
import { fetchLoanDistByPriorityPie } from "../store/reducers/dashboardSlice/api";
import CommonGrid from "./CommonGrid";
import GridLoadError from "./CommonGrid/GridLoadError";

const PieChartWithTable = ({
  fetchFunc = fetchLoanDistByPriorityPie,
  fetchKey = "loanDistByPriorityPie",
  title,
  headers,
  pieLabel,
  pieValue,
  formOption,
  handleClick,
  filterKey = "filterKey",
  commonTable = false,
  index = 0
}) => {
  const { data, status, retry } = useFetchTableData(fetchFunc, fetchKey);
  const total = formOption ? formOption.total : data.total;
  const columnData = formOption ? formOption.resp : data.resp;
  const status2 = formOption ? formOption.status : status;
  const pieData = createPieData(columnData, pieLabel, pieValue);
  return (
    <div className="col-sm-4 col-lg-12">
      <div className="h-100 bg-white border-radius-bottom--4">
        <div className=" fw-bold p-5 border-bottom">{title}</div>
        {status2 === "fulfilled" ? (
          <div className="row gx-0">
            {commonTable ? null : (
              <div className="col-2 d-flex justify-content-center mt-2">
                {total?.amount === 0 ? (
                  <p className="text-center">No Preview</p>
                ) : (
                  <Pie data={pieData} options={options} />
                )}
              </div>
            )}
            <div className={commonTable ? "" : "col-10"}>
              {commonTable ? (
                <CommonGrid
                  gridColumns={headers}
                  columnData={columnData}
                  header={false}
                  minHeight={"100%"}
                  tHeight={""}
                  tableTotal={["amount"]}
                  showTotal={true}
                />
              ) : (
                <PieChartTable
                  headers={headers}
                  columnData={columnData}
                  total={total}
                  handleClick={
                    handleClick
                      ? (value) =>
                          handleClick({ title, value: value.value, filterKey, index })
                      : null
                  }
                />
              )}
            </div>
          </div>
        ) : status2 === "failed" && !formOption ? (
          <GridLoadError title={title} onRetry={retry} />
        ) : (
          <SpinnerBlock />
        )}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
          <div></div>
          {!!formOption && <FormBox {...formOption} />}
        </div>
      </div>
    </div>
  );
};
export default PieChartWithTable;
export const backgroundColors = [
  "rgba(255, 99, 132, 1)",
  "rgba(54, 162, 235, 1)",
  "rgba(255, 206, 86, 1)",
  "rgba(75, 192, 192, 1)",
  "rgba(153, 102, 255, 1)",
  "rgba(255, 159, 64, 1)",
  "rgba(0, 0, 255, 1)", // Pure Blue
  "rgba(75, 192, 192, 1)", // Bright Cyan
  "rgba(255, 206, 86, 1)", // Bright Yellow
  "rgba(255, 69, 0, 1)", // Red-Orange
  "rgba(255, 159, 64, 1)", // Bright Orange
  "rgba(106, 90, 205, 1)", // Slate Blue
  "rgba(0, 255, 0, 1)", // Lime Green
  "rgba(54, 162, 235, 1)", // Bright Blue
  "rgba(255, 0, 0, 1)", // Pure Red
  "rgba(60, 179, 113, 1)", // Medium Sea Green
  "rgba(218, 165, 32, 1)", // Golden Rod
  "rgba(0, 255, 127, 1)", // Spring Green
  "rgba(255, 105, 180, 1)", // Hot Pink
  "rgba(255, 20, 147, 1)", // Deep Pink
  "rgba(255, 99, 132, 1)", // Bright Red
  "rgba(238, 130, 238, 1)", // Vibrant Pink
  "rgba(153, 102, 255, 1)", // Bright Purple
];

const options = {
  plugins: {
    legend: { display: false },
  },
};

export const createPieData = (columnData, pieLabel, pieValue) => {
  return {
    labels: columnData?.map((item) => item?.[pieLabel] ?? "Unknown"),
    datasets: [
      {
        label: "",
        data: columnData?.map((item) => item?.[pieValue] ?? 0),
        backgroundColor: backgroundColors,
        radius: "90%",
      },
    ],
  };
};
// --------------------------
const PieChartTable = ({ headers, columnData, total, handleClick }) => {
  const TableHeader = ({ headers }) => (
    <thead>
      <tr style={{ background: "#fbfbfb" }}>
        {headers?.map((header) => (
          <th key={header.field}>{header.title}</th>
        ))}
      </tr>
    </thead>
  );

  const formatTableCell = (header, value, index, color) => {
    if (header.field === "amount") {
      return formatNumber({ prefix: "$", round: 2 })(
        parseFloat(value || 0).toFixed(2)
      );
    } else if (header.field === "percent") {
      return value > 100 ? "100.00%" : `${value || 0}%`;
    } else if (index === 0 && value) {
      return (
        <div
          role={handleClick && "button"}
          onClick={() => handleClick && handleClick({ value })}
        >
          <svg width="10" height="10" fill={color}>
            <rect width="10" height="10" />
          </svg>{" "}
          {handleClick ? (
            <span
              style={{
                display: "inline-block",
                color: "#438ffe",
                border: "none",
                borderBottom: "1px solid currentColor",
              }}
            >
              {" "}
              {value}
            </span>
          ) : (
            value
          )}
        </div>
      );
    }
    return value;
  };

  const TableRow = ({ row, headers, color }) => (
    <tr>
      {headers?.map((header, index) => (
        <td key={`${row.id}-${header.field}`}>
          {formatTableCell(header, row[header.field], index, color)}
        </td>
      ))}
    </tr>
  );

  return (
    <Table bordered style={{ fontSize: "14px" }}>
      <TableHeader headers={headers} />
      <tbody>
        {columnData?.map((row, index) => (
          <TableRow
            key={index}
            row={row}
            headers={headers}
            color={backgroundColors[index]}
          />
        ))}
        <tr className="fw-bold" style={{ background: "#fbfbfb" }}>
          {headers?.map((header, index2) => (
            <td key={`${index2}-${header.field}`}>
              {formatTableCell(header, total?.[header.field], index2)}
            </td>
          ))}
        </tr>
      </tbody>
    </Table>
  );
};
